import { ExternalToolsIcon } from '@/shared/ui/GeneralIcons';
import { LayoutDashboardIcon, PlusIcon, TagIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Separator,
} from '@monksflow/monks-ui';

import { TabLinkButton } from './TabLinkButton';
import {
  CategoriesPanel,
  ExternalToolsOverview,
  ExternalToolsPanel,
} from '@/features/admin/ui/ExternalTools';
import { Loading } from '@/shared/ui';
import type { AgentToolCategoryOption } from '@/features/admin/components/AgentToolBasicInformation';
import type { CategoryId } from '@/shared/constants/category-colors';
import { WorkspaceScopeTabs } from './WorkspaceScopeTabs';
import { useExternalToolsPage } from './useExternalToolsPage';
import { useTranslation } from 'react-i18next';

type MainTab = 'overview' | 'tools' | 'categories';
type ScopeTab = string;

// The scope suffix ("for the Organization" / "for this workspace") is baked into
// each subtitle rather than appended, so a locale can reorder or inflect it.
const TAB_META = {
  overview: {
    titleKey: 'admin.externalToolsPage.overview',
    subtitleKey: 'admin.externalToolsPage.configurationOverviewAndStatistics',
  },
  tools: {
    titleKey: 'admin.externalToolsPage.externalTools',
    organizationSubtitleKey: 'admin.externalToolsPage.addAndManageExternalToolsForTheOrganization',
    workspaceSubtitleKey: 'admin.externalToolsPage.addAndManageExternalToolsForThisWorkspace',
    addLabelKey: 'admin.externalToolsPage.addExternalTool',
  },
  categories: {
    titleKey: 'admin.externalToolsPage.categories',
    organizationSubtitleKey:
      'admin.externalToolsPage.manageExternalToolCategoriesForTheOrganization',
    workspaceSubtitleKey: 'admin.externalToolsPage.manageExternalToolCategoriesForThisWorkspace',
    addLabelKey: 'admin.externalToolsPage.addCategory',
  },
} as const satisfies Record<MainTab, unknown>;

interface ExternalToolsPageProps {
  toolName?: string;
  toolDescription?: string;
  onSaveBasicInfo?: (
    data: { name: string; description: string; category?: CategoryId },
    workspaceId?: string,
  ) => Promise<void>;
  onScopeChange?: (scope: ScopeTab) => void;
  category?: CategoryId;
  categoryOptions?: readonly AgentToolCategoryOption[];
}

export const ExternalToolsPage = ({
  toolName,
  toolDescription,
  onSaveBasicInfo,
  onScopeChange,
  category,
  categoryOptions,
}: ExternalToolsPageProps) => {
  const { t } = useTranslation('admin');

  const {
    activeTab,
    setActiveTab,
    activeScope,
    isOrganizationTab,
    handleScopeChange,
    currentWorkspace,
    otherWorkspaces,
    workspaceIdParam,
    tools,
    toolsLoading,
    toolsFetching,
    allCategories,
    categoriesLoading,
    addToolOpen,
    setAddToolOpen,
    addCategoryOpen,
    setAddCategoryOpen,
    handleAddClick,
    handleSaveBasicInfo,
    orgRenameConfirmOpen,
    handleConfirmOrgRename,
    handleCancelOrgRename,
  } = useExternalToolsPage({ onSaveBasicInfo, onScopeChange });

  const tabMeta = TAB_META[activeTab];
  const title = t(tabMeta.titleKey);
  const subtitle =
    'subtitleKey' in tabMeta
      ? t(tabMeta.subtitleKey)
      : t(isOrganizationTab ? tabMeta.organizationSubtitleKey : tabMeta.workspaceSubtitleKey);
  const addLabel = 'addLabelKey' in tabMeta ? t(tabMeta.addLabelKey) : undefined;

  return (
    <div className="flex w-full flex-col">
      {/* Workspace Scope Tabs */}
      <WorkspaceScopeTabs
        activeScope={activeScope}
        onScopeChange={handleScopeChange}
        currentWorkspace={currentWorkspace}
        otherWorkspaces={otherWorkspaces}
      />

      {/* Main Content */}
      <div className="flex flex-row items-start gap-6 pt-6">
        {/* Sidebar nav */}
        <div className="flex flex-col gap-4 rounded-2xl border border-border p-6">
          <div className="flex w-45 shrink-0 flex-col gap-2 pt-1">
            <TabLinkButton
              onClick={() => setActiveTab('overview')}
              isActive={activeTab === 'overview'}
              icon={LayoutDashboardIcon}
              centered={false}
            >
              {t('admin.externalToolsPage.overview')}
            </TabLinkButton>
            <Separator decorative={false} className="my-1" />
            <TabLinkButton
              onClick={() => setActiveTab('categories')}
              isActive={activeTab === 'categories'}
              icon={TagIcon}
              centered={false}
            >
              {t('admin.externalToolsPage.categories')}
            </TabLinkButton>
            <TabLinkButton
              onClick={() => setActiveTab('tools')}
              isActive={activeTab === 'tools'}
              icon={ExternalToolsIcon}
              centered={false}
            >
              {t('admin.externalToolsPage.externalTools')}
            </TabLinkButton>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col gap-4">
          {activeTab !== 'overview' && (
            <div className="flex flex-row items-start justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-medium leading-7 text-foreground">{title}</h2>
                <p className="text-sm leading-5 text-muted-foreground">{subtitle}</p>
              </div>
              {addLabel &&
                ((activeTab === 'tools' && tools.length !== 0) ||
                  (activeTab === 'categories' && allCategories.length !== 0)) && (
                  <Button type="button" onClick={handleAddClick}>
                    <PlusIcon /> {addLabel}
                  </Button>
                )}
            </div>
          )}

          {/* Bordered content box */}
          <div
            className={`flex flex-col rounded-2xl border border-border ${
              activeTab === 'overview' ? '' : 'gap-4 p-4'
            } ${
              allCategories.length === 0 && !toolsLoading && activeTab === 'tools'
                ? 'bg-warning'
                : ''
            }`}
          >
            {toolsLoading ? (
              <Loading />
            ) : activeTab === 'overview' ? (
              <ExternalToolsOverview
                tools={tools}
                allCategories={allCategories}
                toolsLoading={toolsLoading}
                categoriesLoading={categoriesLoading}
                onNavigate={setActiveTab}
                toolName={toolName}
                toolDescription={toolDescription}
                onSaveBasicInfo={onSaveBasicInfo ? handleSaveBasicInfo : undefined}
                workspaceId={workspaceIdParam}
                category={category}
                categoryOptions={categoryOptions}
              />
            ) : activeTab === 'tools' ? (
              <ExternalToolsPanel
                tools={tools}
                toolsLoading={toolsLoading}
                toolsFetching={toolsFetching}
                allCategories={allCategories}
                addOpen={addToolOpen}
                onAddOpenChange={setAddToolOpen}
                workspaceId={workspaceIdParam}
              />
            ) : (
              <CategoriesPanel
                allCategories={allCategories}
                categoriesLoading={categoriesLoading}
                tools={tools}
                addOpen={addCategoryOpen}
                onAddOpenChange={setAddCategoryOpen}
                workspaceId={workspaceIdParam}
              />
            )}
          </div>
        </div>
      </div>

      <Dialog open={orgRenameConfirmOpen} onOpenChange={(open) => !open && handleCancelOrgRename()}>
        <DialogContent className="gap-0 p-4" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              {t('admin.externalToolsPage.updateToolForEntireOrganization')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="py-6">
            <p className="text-sm text-muted-foreground">
              {t('admin.externalToolsPage.youReEditingThisTool')}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelOrgRename}>
              {t('admin.externalToolsPage.cancel')}
            </Button>
            <Button variant="default" onClick={() => void handleConfirmOrgRename()}>
              {t('admin.externalToolsPage.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
